import * as React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, DatePicker, Input, Row, Tooltip, Modal, Form } from "antd";
import moment from "moment";
import { v4 as uuid } from "uuid";
import { ReactComponent as CancelButtonIcon } from "../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as SubmitButtonIcon } from "../../img/buttons_icons/submitButton_icon.svg";
import { ReactComponent as CloseVersionIcon } from "../../img/versions_icons/closeVersion_icon.svg";
import { ReactComponent as AddVersionIcon } from "../../img/versions_icons/addVersion_icon.svg";
import { ReactComponent as EditIcon } from "../../img/versions_icons/editVersion_icon.svg";
import {
  displayFormat,
  toDisplayFormat,
  toHttpFormat,
  toHttpFormatStartOfDay,
} from "../../lib/utils/formatDate";
import "../../styles/row.less";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ACCOUNT_ACCOUNTINFO_WRITE } from "../../lib/const/permissions";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";

export default function AccountEmailsVersionsTable(props) {
  const { fetchEmailVersions, handleCreateEmailVersion, handleCloseEmailVersion } = props;
  const {
    accountEmails,
    accountEmailsLoading,
    emailsVersions,
    emailsVersionsLoading,
  } = useSelector((state) => state.accounts);
  const currentDate = moment();
  const [childTableData, setChildTableData] = useState([]);
  const [tableData, setTableData] = useState([]);
  

  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);

  const [form] = Form.useForm();

  const handleSetInitialValue = (field) => {
    setTimeout(() => {
      form.setFieldsValue(field);
    }, 0);
  };

  useEffect(() => {
    if (checkIsLoaded(accountEmailsLoading)) {
      setTableData(
        accountEmails
          .map((item) => ({
            key: item.id,
            id: item.id,
            email: item.email,
            fromDate: item.fromDate,
            toDate: item.toDate,
          }))
          .sort((a, b) => {
            if (a.fromDate > b.fromDate) {
              return -1;
            }
            if (a.fromDate < b.fromDate) {
              return 1;
            }
            return 0;
          })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountEmailsLoading]);

  useEffect(() => {
    if (checkIsLoaded(emailsVersionsLoading)) {
      setChildTableData(
        emailsVersions[0]
          .map((version, i) => ({
            key: `${version.id}${version.fromDate}`,
            id: version.id,
            email: version.email,
            fromDate: version.fromDate,
            toDate: version.toDate,
            isCurrentVersion:
              moment(currentDate).isSameOrAfter(version.fromDate) &&
              moment(currentDate).isBefore(version.toDate),
            isPastVersion: moment(version?.toDate).isBefore(currentDate),
            isLatestVersion: emailsVersions[0].length === i + 1,
            isClosing: false,
            isCreated: false,
          }))
          .sort((a, b) => (moment(a.fromDate).isBefore(moment(b.fromDate)) ? 1 : -1))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailsVersionsLoading]);

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    email: "",
  };

  const [columns, setColumns] = useState([
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: columnsWidths.email,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (text, record) =>
        `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`,
    },
    {
      title: "",
      dataIndex: "actions",
      width: columnsWidths.actions,
      key: "actions",
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: columnsWidths.email,
      render(text, record) {
        if (record.isCreated || record.isEditing) {
          handleSetInitialValue({ email: text });
          return (
            <Form.Item
              style={{ marginBottom: 0 }}
              name="email"
              className="form-item-required__hidden-error__mb0"
              rules={[{ required: true, message: "Введите корректный e-mail", type: "email" }]}
              // initialValue={text}
            >
              <Input />
            </Form.Item>
          );
        }
        return record.email;
      },
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render(text, record) {
        if (record.isCreated) {
          handleSetInitialValue({ fromDate: moment(record.fromDate).add(1, "day") });
          return (
            <>
              <Form.Item
                style={{ display: "inline-block" }}
                className="form-item-required__hidden-error__mb0"
                name="fromDate"
                rules={[{ required: true }]}
              >
                <DatePicker
                  format={displayFormat}
                  allowClear={false}
                  disabledDate={(current) =>
                    current <= moment(record.fromDate) || current > moment(record.toDate)
                  }
                  // defaultPickerValue={moment(record.fromDate).add(1, "day")}
                />
              </Form.Item>
              <Form.Item
                style={{ display: "inline-block" }}
                className="form-item-required__hidden-error__mb0"
              >
                - {toDisplayFormat(record.toDate)}
              </Form.Item>
            </>
          );
        }
        if (record.isClosing) {
          handleSetInitialValue({ toDate: moment(currentDate) });
          return (
            <>
              <Form.Item
                style={{ display: "inline-block", marginRight: "5px" }}
                className="form-item-required__hidden-error__mb0"
              >
                {toDisplayFormat(record.fromDate)} -
              </Form.Item>
              <Form.Item
                style={{ display: "inline-block" }}
                name="toDate"
                rules={[{ required: true }]}
                className="form-item-required__hidden-error__mb0"
              >
                <DatePicker format={displayFormat} allowClear={false} disabled />
              </Form.Item>
            </>
          );
        }
        if (record.fromDate && record.toDate) {
          return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
        }
        return "";
      },
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      render: (value, record) => {
        if (hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE)) {
          if (
            !record.isPastVersion &&
            !record.isCreated &&
            !record.isClosing &&
            !record.isEditing
          ) {
            return (
              <Row>
                <Tooltip title="Добавить версию">
                  <Button
                    className="button-icon"
                    icon={<AddVersionIcon />}
                    type="text"
                    htmlType="button"
                    disabled={isChangingForbidden}
                    onClick={(e) => {
                      e.stopPropagation();
                      setChildTableData((prevState) =>
                        [
                          {
                            ...record,
                            key: uuid(),
                            fromDate: moment(record.fromDate).add(1, "seconds"),
                            isCreated: true,
                            isCurrentVersion: false,
                            isPastVersion: false,
                            isLatestVersion: false,
                          },
                          ...prevState,
                        ].sort((a, b) => (moment(a.fromDate).isBefore(moment(b.fromDate)) ? 1 : -1))
                      );
                    }}
                  />
                </Tooltip>
                <Tooltip title="Редактировать версию">
                  <Button
                    className="button-icon"
                    icon={<EditIcon />}
                    type="text"
                    htmlType="button"
                    disabled={isChangingForbidden}
                    onClick={(e) => {
                      e.stopPropagation();
                      setChildTableData((prevState) =>
                        prevState.map((item) => {
                          if (item.key === record.key) return { ...item, isEditing: true };
                          return item;
                        })
                      );
                    }}
                  />
                </Tooltip>
                {record.isLatestVersion && (
                  <Tooltip title="Изменить дату закрытия">
                    <Button
                      className="button-icon"
                      icon={<CloseVersionIcon />}
                      type="text"
                      htmlType="button"
                      disabled={isChangingForbidden}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (tableData.length === 1) {
                          Modal.warning({
                            title: "Закрытие e-mail",
                            content:
                              "Для корректной работы системы у аккаунта должен быть задан хотя бы один e-mail",
                          });
                        } else {
                          setChildTableData((prevState) =>
                            prevState.map((item) => {
                              if (item.key === record.key) return { ...item, isClosing: true };
                              return item;
                            })
                          );
                        }
                      }}
                    />
                  </Tooltip>
                )}
              </Row>
            );
          }
          if (record.isCreated || record.isClosing || record.isEditing) {
            return (
              <Row>
                <Tooltip title="Подтвердить">
                  <Button
                    className="button-icon"
                    icon={<SubmitButtonIcon />}
                    type="text"
                    htmlType="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      form.submit();
                    }}
                  />
                </Tooltip>
                <Tooltip title="Отменить">
                  <Button
                    className="button-icon"
                    icon={<CancelButtonIcon />}
                    type="text"
                    htmlType="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (record.isCreated) {
                        setChildTableData((prevState) => prevState.filter((obj) => !obj.isCreated));
                      }
                      if (record.isEditing) {
                        setChildTableData((prevState) =>
                          prevState.map((item) => {
                            if (item.isEditing) {
                              return { ...item, isEditing: false };
                            }
                            return item;
                          })
                        );
                      }
                      if (record.isClosing) {
                        setChildTableData((prevState) =>
                          prevState.map((item) => {
                            if (item.isClosing) {
                              return { ...item, isClosing: false };
                            }
                            return item;
                          })
                        );
                      }
                      form.resetFields();
                    }}
                  />
                </Tooltip>
              </Row>
            );
          }
        }
        return null;
      },
    },
    {
      title: "",
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ];

  const [childColumns, setChildColumns] = useState(getChildColumns());

  useEffect(() => {
    setChildColumns(getChildColumns());
  }, [isChangingForbidden, tableData]);

  const onFinish = (values) => {
    const version = childTableData.find(
      (item) => item.isCreated || item.isEditing || item.isClosing
    );

    if (version.isClosing) {
      handleCloseEmailVersion(version.id, toHttpFormat(values.toDate));
    } else {
      const force = Boolean(version.isEditing);
      const fromDate = force ? String(version.fromDate) : toHttpFormatStartOfDay(values.fromDate);
      const emailData = {
        id: version.id,
        email: values.email,
      };
      handleCreateEmailVersion({
        fromDate,
        emailData,
        force,
      });
    }
  };

  return (
    <Form form={form} onFinish={onFinish}>
      <ToggleableVersionsTable
        tableName="organization-emails-table"
        columns={columns}
        setColumns={setColumns}
        childTableColumns={childColumns}
        setChildTableColumns={setChildColumns}
        tableData={tableData}
        childTableData={childTableData}
        isChildTableLoading={checkIsLoading(emailsVersionsLoading)}
        tableClassName="organization-emails-table"
        isPageable={false}
        isToggleableColumns={false}
        onOpenVersionsCallback={(record) => {
          fetchEmailVersions(record.id);
        }}
      />
    </Form>
  );
}
